import ClearIcon from "@mui/icons-material/Clear";
import {
  Badge,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import classNames from "classnames";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DateRangePicker } from "react-date-range";
import { useDispatch, useSelector } from "react-redux";
import CompareIconCircle from "../../assets/svgs/CompareIconCircle.svg";
import CompareIconClose from "../../assets/svgs/CompareIconClose.svg";
import CompareSideIcon from "../../assets/svgs/CompareSideIcon.svg";
import { getAccountFacility } from "../../services/facility.service";
import {
  setActiveComparisonAccountId,
  setActiveComparisonFacilitiesId,
  setcomparingAgainstDifferentFacility,
  setComparisonRanges,
  setFacilityByAccount,
  setIsComparisonRangeSet,
  setRefetchComparison,
  setRemoveCompareFilter,
} from "../../store/reducers/comparisonReducers/comparison.slice";
import { areArraysDifferent } from "../../utilis/common";
import HtmlTooltip from "../shared/HtmlTooltip";
import styles from "./ComparisonPopup.module.scss";
import useComparisonFunctions from "./useComparisonFunctions";
import { toDisplayFullDateWithNextDay } from "../../utilis/date-formats";
import useFacilityId from "../../hooks/useFacilityId";
import useAccountId from "../../hooks/useAccountId";

// Helper to serialize dates for Redux (converts Date objects to ISO strings)
const serializeRangesForRedux = (ranges) => {
  if (!ranges) return ranges;
  return ranges.map(range => ({
    ...range,
    startDate: range.startDate instanceof Date ? range.startDate.toISOString() : range.startDate,
    endDate: range.endDate instanceof Date ? range.endDate.toISOString() : range.endDate,
  }));
};

// Helper to deserialize dates from Redux (converts ISO strings to Date objects for DateRangePicker)
const deserializeRangesFromRedux = (ranges) => {
  if (!ranges) return ranges;
  return ranges.map(range => ({
    ...range,
    startDate: typeof range.startDate === 'string' ? new Date(range.startDate) : range.startDate,
    endDate: typeof range.endDate === 'string' ? new Date(range.endDate) : range.endDate,
  }));
};

const ComparisonPopup = ({
  forPage,
  loading,
  setLoadingComparison,
  isOpenFromParent = false,
  type = "default",
  setIsOpenFromParent,
  selectedSortCut,
  isMobile,
}) => {
  const [open, setOpen] = useState(false);

  const dispatch = useDispatch();
  const {
    comparisonRanges: ranges,
    rangesSet,
    resetComparison,
    isUpdatedDateManually,
    manuallyClickComparison,
  } = useSelector((state) => state.comparison);
  const pauseDate = useSelector((state) => state.common.pauseDate);
  
  const [filter, setFilter] = useState(deserializeRangesFromRedux(ranges));
  const [gettingFacility, setGettingFacility] = useState(false);
  const anchorRef = useRef(null);
  const { getAllDataCount } = useComparisonFunctions({
    forPage,
    setLoadingComparison,
  });
  const { facilityId } = useFacilityId();
  const { accountId: accountIdFromHook } = useAccountId();
  const accountId = accountIdFromHook;
  const { account: accounts } = useSelector(
    ({ auth, account, permission }) => ({
      auth,
      account,
      permission,
    })
  );
  const activeFacilities = useSelector(
    (state) => state.activeFacilities.facilities
  );

  // managing facilities for comparison
  const activeComparisonFacilities = useSelector(
    (state) => state.comparison.activeComparisonFacilitiesId
  );
  const [
    localActiveComparisonFacilitiesId,
    setLocalActiveComparisonFacilitiesId,
  ] = useState([]);
  const [localFacilitiesId, setLocalFacilitiesId] = useState([]);

  // managing active account for comparison
  const activeAccountId = useSelector(
    (state) => state.comparison.activeComparisonAccountId
  );
  const [localActveAccountId, setLocalActiveAccountId] = useState([]);
  const [isUpdatedDate, setIsUpdatedDate] = useState(false);

  useEffect(() => {
    setIsUpdatedDate(isUpdatedDateManually);
  }, [isUpdatedDateManually]);

  const handleClose = () => {
    setLocalActiveComparisonFacilitiesId([]);
    setLocalFacilitiesId([]);
    setLocalActiveAccountId(null);
    setOpen(false);
    setIsOpenFromParent && setIsOpenFromParent(false);
  };

  useEffect(() => {
    if (isOpenFromParent) {
      setOpen(true);
    }
  }, [isOpenFromParent]);

  const handleCalendarRangeChange = (item) => {
    setIsUpdatedDate(true);
    const newRange = {
      ...item.selection,
      startDate: new Date(item.selection.startDate),
      endDate: new Date(item.selection.endDate),
    };
    setFilter([newRange]);
  };

  const toggleOpenState = () => {
    setOpen((open) => !open);
    setIsOpenFromParent && setIsOpenFromParent(false);
  };

  const onSaveButton = (
    { range = null, isDefault = false },
    type = "default"
  ) => {
    if (type === "default") {
      dispatch(setIsComparisonRangeSet(true));
    }
    if (isDefault) {
      const diffFacilities = areArraysDifferent(
        localActiveComparisonFacilitiesId,
        activeFacilities
      );
      diffFacilities
        ? dispatch(setcomparingAgainstDifferentFacility(true))
        : dispatch(setcomparingAgainstDifferentFacility(false));

      dispatch(setActiveComparisonAccountId(accountId));
      dispatch(setActiveComparisonFacilitiesId(activeFacilities));
      setIsUpdatedDate(true);
      setFilter(deserializeRangesFromRedux(range));
      dispatch(setComparisonRanges(serializeRangesForRedux(range ? range : filter)));
      dispatch(setcomparingAgainstDifferentFacility(false));
    } else {
      const diffFacilities = areArraysDifferent(
        localActiveComparisonFacilitiesId,
        activeFacilities
      );
      diffFacilities
        ? dispatch(setcomparingAgainstDifferentFacility(true))
        : dispatch(setcomparingAgainstDifferentFacility(false));

      dispatch(setActiveComparisonAccountId(localActveAccountId));
      dispatch(
        setActiveComparisonFacilitiesId(localActiveComparisonFacilitiesId)
      );
      dispatch(setComparisonRanges(serializeRangesForRedux(range ? range : filter)));
      dispatch(setRefetchComparison(true));
      setOpen(false);
      setIsOpenFromParent && setIsOpenFromParent(false);
    }
  };

  useMemo(() => {
    if (manuallyClickComparison) {
      onSaveButton(manuallyClickComparison, "manual");
    }
  }, [manuallyClickComparison]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOnChangeAccount = useCallback(
    async (accountId) => {
      setGettingFacility(true);
      setLocalActiveAccountId(accountId);
      const res = await getAccountFacility(accountId);

      if (res && res.length > 0) {
        setLocalFacilitiesId([...res]);
      }
      setGettingFacility(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setLocalActiveComparisonFacilitiesId]
  );

  const facilityCheckboxOnChange = useCallback(
    (e, item) => {
      const checked = e.target.checked;
      dispatch(setFacilityByAccount(localFacilitiesId));
      if (checked) {
        setLocalActiveComparisonFacilitiesId((oldState) => {
          return [...oldState, item._id];
        });
      } else {
        setLocalActiveComparisonFacilitiesId((oldState) => {
          return oldState.filter((x) => x !== item._id);
        });
      }
    },
    [dispatch, localFacilitiesId]
  );

  // initial account loadin
  useEffect(() => {
    if (activeAccountId && !localActveAccountId && open) {
      setLocalActiveAccountId(activeAccountId);
      handleOnChangeAccount(activeAccountId);
    } else if (!activeAccountId && localActveAccountId && open) {
      dispatch(setActiveComparisonAccountId(localActveAccountId));
      setLocalActiveComparisonFacilitiesId(activeFacilities);
      handleOnChangeAccount(localActveAccountId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeAccountId,
    activeComparisonFacilities,
    activeFacilities,
    dispatch,
    localActveAccountId,
    open,
  ]);

  useEffect(() => {
    setFilter(deserializeRangesFromRedux(ranges));
  }, [ranges]);

  useEffect(() => {
    handleOnChangeAccount(accountId);
    // we want those facilities fetched only when initially the popup opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const filterUpdated = ranges ? ranges[0] : null;
    if (
      filterUpdated &&
      isUpdatedDate &&
      filterUpdated.startDate &&
      facilityId
    ) {
      rangesSet && getAllDataCount && getAllDataCount(filterUpdated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ranges, facilityId, activeFacilities, activeComparisonFacilities]);

  const handleCloseDateRange = useCallback(() => {
    dispatch(setRemoveCompareFilter(false));
  }, [dispatch]);

  // initial facilities loadin
  useEffect(() => {
    if (activeComparisonFacilities?.length > 0) {
      setLocalActiveComparisonFacilitiesId(activeComparisonFacilities);
    } else {
      setLocalActiveComparisonFacilitiesId(activeFacilities);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeComparisonFacilities]);

  useEffect(() => {
    if (resetComparison) {
      dispatch(setActiveComparisonAccountId(null));
      handleCloseDateRange();
    }
  }, [dispatch, handleCloseDateRange, resetComparison]);

  return (
    <>
      {type === "openFromParent" &&
        !selectedSortCut?.isCompare &&
        !isMobile && <div ref={anchorRef} />}

      {selectedSortCut?.isCompare && !isMobile && (
        <>
          {rangesSet ? (
            <Badge
              color={!loading ? "error" : undefined}
              sx={{
                cursor: loading ? "default" : "pointer",
                "& .MuiBadge-badge": {
                  minWidth: "12px", // Smaller width for the badge
                  height: "12px", // Smaller height for the badge
                  borderRadius: "50%", // Make the badge round
                  transform: "translate(50%, -50%) scale(1.2)", // Position and scale down
                  padding: 0, // Remove padding for a snug fit
                },
              }}
              badgeContent={
                !loading ? (
                  <HtmlTooltip
                    content="Remove Compare Filter"
                    placement="bottom-start"
                  >
                    <ClearIcon
                      onClick={() => {
                        dispatch(setIsComparisonRangeSet(false));
                        handleCloseDateRange();
                      }}
                      sx={{
                        fontSize: 10,
                      }}
                    />
                  </HtmlTooltip>
                ) : (
                  <div
                    className="skeleton"
                    style={{
                      width: "16px",
                      height: "16px",
                      borderRadius: "10px",
                    }}
                  ></div>
                )
              }
            >
              <Tooltip title={"Trend Analysis (Compare and Highlight)"} arrow>
                <>
                  <IconButton
                    aria-label="Compare & Highlight"
                    size="small"
                    onClick={loading ? undefined : toggleOpenState}
                    ref={anchorRef}
                    sx={{
                      width: 30,
                      height: 30,
                      ml: 1,
                      backgroundColor: "#4879f5",
                      borderRadius: "4px",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.12)",
                      },
                      color: "#fff",
                    }}
                  >
                    <CompareSideIcon width={18} height={18} />
                  </IconButton>
                </>
              </Tooltip>
            </Badge>
          ) : (
            <Tooltip title="Trend Analysis (Compare and Highlight)" arrow>
              <IconButton
                aria-label="Compare & Highlight"
                size="small"
                onClick={loading ? undefined : toggleOpenState}
                ref={anchorRef}
                sx={{
                  width: 30,
                  height: 30,
                  ml: 1,
                  borderRadius: "4px",
                }}
              >
                <CompareSideIcon width={18} height={18} />
              </IconButton>
            </Tooltip>
          )}
        </>
      )}
      <Popover
        id="comparison-popover"
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={{
          ...(isMobile && {
            "& .MuiPopover-paper": {
              width: "100vw",
              height: "100vh",
              maxWidth: "100vw",
              maxHeight: "100vh",
              margin: 0,
              borderRadius: 0,
              top: "0 !important",
              left: "0 !important",
              transform: "none !important",
            },
          }),
        }}
      >
        <Box className={styles.mainWrapper}>
          <Box className={styles.headerWrapper}>
            <div className={styles.header}>
              <div className={styles.compareIconInnerWrapper}>
                <CompareIconCircle />
              </div>
              <Typography variant="h6">
                Trend Analysis (Compare and Highlight)
              </Typography>
            </div>
            <div
              className={styles.compareIconCloseWrapper}
              onClick={handleClose}
            >
              <CompareIconClose />
            </div>
          </Box>
          <Box className={styles.accountsWrapper}>
            {!isMobile && <Typography>Accounts:</Typography>}
            {isMobile ? (
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputLabel id="account-select-label">
                  Select Account
                </InputLabel>
                <Select
                  labelId="account-select-label"
                  id="account-select"
                  value={localActveAccountId || ""}
                  label="Select Account"
                  onChange={(e) => handleOnChangeAccount(e.target.value)}
                  size="medium"
                >
                  {accounts?.length > 0 &&
                    accounts.map((item) => (
                      <MenuItem key={item._id} value={item._id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  <MenuItem value={1}>Chose All Accounts</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <div className={styles.accounts}>
                {accounts?.length > 0 &&
                  accounts.map((item, index) => {
                    return (
                      <Fragment key={item._id}>
                        <Button
                          key={`account-button-${index}`}
                          className={`${
                            item._id === localActveAccountId ? "selected" : null
                          }`}
                          onClick={() => handleOnChangeAccount(item._id)}
                          variant="contained"
                          sx={{
                            mr: 1,
                            mb: 1,
                            background: "#eef3f7",
                            color: "#000",
                            cursor: "pointer",
                            "&:hover": {
                              color: "#fff",
                            },
                            "&.selected": {
                              color: "#fff",
                              background: "#103996",
                            },
                          }}
                        >
                          {item.name}
                        </Button>
                      </Fragment>
                    );
                  })}
                <Button
                  onClick={() => setLocalActiveAccountId(1)}
                  variant="contained"
                  size="medium"
                  sx={{
                    mr: 1,
                    mb: 1,
                    background: "#eef3f7",
                    color: "#000",
                    cursor: "pointer",
                  }}
                >
                  Chose All Accounts
                </Button>
              </div>
            )}
          </Box>
          {!isMobile && <Divider />}
          <Box className={styles.contentWrapper}>
            {isMobile ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateRows: "auto auto auto",
                  gap: 1,
                  width: "100%",
                }}
              >
                <Box sx={{ width: "100%" }}>
                  <FormControl fullWidth sx={{ mb: 1 }}>
                    <InputLabel id="facilities-select-label">
                      Select Account Facilities
                    </InputLabel>
                    <Select
                      labelId="facilities-select-label"
                      id="facilities-select"
                      multiple
                      size="medium"
                      value={localActiveComparisonFacilitiesId}
                      label="Select Facilities"
                      onChange={(e) => {
                        const selectedIds = e.target.value;
                        setLocalActiveComparisonFacilitiesId(selectedIds);
                        dispatch(setFacilityByAccount(localFacilitiesId));
                      }}
                      renderValue={(selected) => {
                        const selectedFacilities = localFacilitiesId.filter(
                          (facility) => selected.includes(facility._id)
                        );
                        return selectedFacilities
                          .map((facility) => facility.name)
                          .join(", ");
                      }}
                      fullWidth
                    >
                      {!gettingFacility &&
                        localFacilitiesId?.length > 0 &&
                        localFacilitiesId.map((item) => (
                          <MenuItem key={item._id} value={item._id}>
                            <Checkbox
                              checked={
                                localActiveComparisonFacilitiesId.indexOf(
                                  item._id
                                ) > -1
                              }
                              size="small"
                            />
                            {item.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  {gettingFacility && (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", mb: 1 }}
                    >
                      <CircularProgress />
                    </Box>
                  )}
                  {!gettingFacility &&
                    localActiveComparisonFacilitiesId.length === 0 &&
                    localFacilitiesId.length === 0 && (
                      <Typography sx={{ mb: 1 }}>No facilities</Typography>
                    )}
                </Box>

                <Box
                  className={classNames("no-sidebar-calendar")}
                  sx={{ width: "100%", mt: 1 }}
                >
                  <DateRangePicker
                    ranges={filter}
                    onChange={handleCalendarRangeChange}
                    direction="horizontal"
                    staticRanges={[]}
                    showSelectionPreview={false}
                    showDateDisplay={false}
                    retainEndDateOnFirstSelection={true}
                    months={isMobile ? 1 : 2}
                    inputRanges={[]}
                    maxDate={pauseDate ? new Date(pauseDate) : undefined}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Button
                    onClick={handleClose}
                    variant="outlined"
                    size="small"
                    sx={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={onSaveButton}
                    variant="contained"
                    size="small"
                    sx={{ flex: 1 }}
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                <Box className={styles.facilitiesWrapper}>
                  <Typography sx={{ marginBottom: "20px" }} fontSize={15}>
                    Account Facilities:
                  </Typography>
                  <div className={styles.checkboxWrapper}>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      {gettingFacility && <CircularProgress />}
                      {!gettingFacility &&
                        localActiveComparisonFacilitiesId.length === 0 &&
                        localFacilitiesId.length === 0 && (
                          <Typography>No facilities</Typography>
                        )}
                    </Box>
                    <FormGroup>
                      {!gettingFacility &&
                        localFacilitiesId?.length > 0 &&
                        localFacilitiesId.map((item, index) => {
                          const isChecked =
                            !!localActiveComparisonFacilitiesId.find(
                              (facilityId) => facilityId === item._id
                            );
                          return (
                            <FormControlLabel
                              key={`facility-chk-${index}`}
                              control={
                                <Checkbox
                                  checked={isChecked}
                                  onChange={(e) =>
                                    facilityCheckboxOnChange(e, item)
                                  }
                                />
                              }
                              label={item?.name}
                            />
                          );
                        })}
                    </FormGroup>
                  </div>
                </Box>
                <Box
                  className={classNames(
                    styles.calendarWrapper,
                    "no-sidebar-calendar"
                  )}
                >
                  <div className={styles.buttonContainer}>
                    <div className={styles.saveButton}>
                      <Button
                        onClick={onSaveButton}
                        variant="contained"
                        size="small"
                      >
                        Save
                      </Button>
                    </div>
                    <div className={styles.cancelButton}>
                      <Button
                        onClick={handleClose}
                        className={styles.saveButton}
                        variant="outlined"
                        size="small"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                  <DateRangePicker
                    ranges={filter}
                    onChange={handleCalendarRangeChange}
                    direction="horizontal"
                    staticRanges={[]}
                    showSelectionPreview={false}
                    showDateDisplay={false}
                    retainEndDateOnFirstSelection={true}
                    months={isMobile ? 1 : 2}
                    inputRanges={[]}
                    maxDate={pauseDate ? toDisplayFullDateWithNextDay(pauseDate) : undefined}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default ComparisonPopup;
